/**
 * App root.
 *
 * Responsabilidades:
 *  - Carregar lista de devices periodicamente (auto-refresh).
 *  - Monitorar /health para indicador de "backend online/offline".
 *  - Rotear entre Dashboard / DevicesList / DeviceDetail / Settings
 *    (sem react-router; estado local — Sprint 1 é simples).
 *  - Exibir banner de erro persistente em falhas.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import ErrorBanner from "./components/ErrorBanner.jsx";
import AppShell from "./components/shell/AppShell.jsx";
import { ApiError, alertCount, getHealth, listDevices, logout as apiLogout, setUnauthorizedHandler } from "./lib/api.js";
import { loadConfig, saveConfig } from "./lib/config.js";
import { authedFrom, clearRecovery, clearSession, isRecoveryActive, loadSession } from "./lib/auth.js";
import { defaultViewForRole, viewAllowed } from "./lib/rbac.js";
import { CriticalConfirmProvider } from "./components/ConfirmCriticalAction.jsx";
import Login from "./pages/Login.jsx";
import DashboardNOC from "./pages/DashboardNOC.jsx";
import DeviceDetail from "./pages/DeviceDetail.jsx";
import FirmwareReleases from "./pages/FirmwareReleases.jsx";
import DevicesList from "./pages/DevicesList.jsx";
import FleetExplorer from "./pages/FleetExplorer.jsx";
import Rollouts from "./pages/Rollouts.jsx";
import Settings from "./pages/Settings.jsx";
import ConfigTemplatesPage from "./components/templates/ConfigTemplatesPage.jsx";
import ProvisioningPage from "./pages/ProvisioningPage.jsx";
import InstallationsPage from "./pages/InstallationsPage.jsx";
import InventoryPage from "./pages/InventoryPage.jsx";
import HealthPage from "./pages/HealthPage.jsx";
import TeamsPage from "./pages/TeamsPage.jsx";
import TemplateRulesPage from "./pages/TemplateRulesPage.jsx";
import AuditPage from "./pages/AuditPage.jsx";
import ObservabilityPage from "./pages/ObservabilityPage.jsx";
import AlertsPage from "./pages/AlertsPage.jsx";
import AutomationPage from "./pages/AutomationPage.jsx";
import OtaManagerPage from "./pages/OtaManagerPage.jsx";
import ProductionPanel from "./pages/ProductionPanel.jsx";
import TenantsPage from "./pages/TenantsPage.jsx";
import AccountsPage from "./pages/AccountsPage.jsx";
import SignupPage from "./pages/SignupPage.jsx";
import ClaimDevicePage from "./pages/ClaimDevicePage.jsx";
import PortalUsersPage from "./pages/PortalUsersPage.jsx";
import TenantApprovalPage from "./pages/TenantApprovalPage.jsx";

export default function App() {
  // S3.9E — landing por role: super_admin/bancada → dashboard; tenant → health.
  const [view, setView] = useState(() => defaultViewForRole());
  // SAAS-PORTAL — rota PÚBLICA de cadastro (antes do login). Ativa via #signup
  // ou pelo link "Criar conta" no Login. Não requer sessão.
  const [publicView, setPublicView] = useState(
    () => (typeof window !== "undefined" && window.location.hash === "#signup" ? "signup" : null),
  );
  const [detailDeviceId, setDetailDeviceId] = useState(null);
  const [detailInitialTab, setDetailInitialTab] = useState("cockpit");

  const [config, setConfig] = useState(loadConfig());
  // S3.2 — sessão de usuário (login). SAAS-AUTH-RECOVERY-MODE-1: o ADMIN_TOKEN
  // só autentica quando o modo de recuperação está EXPLICITAMENTE ativo (botão
  // no Login); sozinho no localStorage NÃO bypassa o login.
  const [session, setSession] = useState(loadSession());
  const [recovery, setRecovery] = useState(isRecoveryActive());
  const authed = authedFrom(session, config.adminToken, recovery);
  const [devices, setDevices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [backendOk, setBackendOk] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0); // bumpa para forçar reload do detalhe
  // Sprint 6.1 — contador global de alertas (badge da sidebar).
  const [alertsOpen, setAlertsOpen] = useState(0);

  const acRef = useRef(null);
  const acHealthRef = useRef(null);

  /** Carrega devices + ping health em paralelo. */
  const refresh = useCallback(async () => {
    // S3.2 / SAAS-AUTH-RECOVERY-MODE-1 — sem sessão Bearer E sem recovery ativo
    // não chama a API (ADMIN_TOKEN configurado sozinho NÃO autentica).
    if (!authedFrom(loadSession(), loadConfig().adminToken, isRecoveryActive())) return;
    // Cancela request anterior se ainda em voo
    if (acRef.current) acRef.current.abort();
    if (acHealthRef.current) acHealthRef.current.abort();
    acRef.current = new AbortController();
    acHealthRef.current = new AbortController();

    setRefreshing(true);

    // health em paralelo, falha silenciosa
    getHealth({ signal: acHealthRef.current.signal })
      .then((res) => setBackendOk(res?.ok === true))
      .catch((err) => {
        if (err.name === "AbortError") return;
        setBackendOk(false);
      });

    // devices é o que importa para a UI principal
    try {
      const list = await listDevices({ signal: acRef.current.signal });
      setDevices(Array.isArray(list) ? list : []);
      setError(null);
      setBackendOk(true);
      setLastRefresh(new Date());
    } catch (err) {
      if (err.name === "AbortError") return;
      setError(err instanceof ApiError ? err : new ApiError(err.message));
      // Não zera devices — preserva o último snapshot conhecido
    } finally {
      setRefreshing(false);
      setLoading(false);
      setRefreshKey((k) => k + 1);
    }
  }, []);

  // Primeiro load + ciclo de auto-refresh
  useEffect(() => {
    setLoading(true);
    refresh();
    const ms = Math.max(3, Number(config.refreshSeconds) || 10) * 1000;
    const id = setInterval(refresh, ms);
    return () => clearInterval(id);
  }, [refresh, config.refreshSeconds]);

  // Limpeza ao desmontar
  useEffect(() => {
    return () => {
      acRef.current?.abort();
      acHealthRef.current?.abort();
    };
  }, []);

  // SAAS-AUTH-RETIRE-ADMIN-TOKEN — handler de sessão expirada/inválida. Em 401
  // de uma chamada autenticada com sessão Bearer, o api.js já fez clearSession();
  // aqui sincronizamos o estado React para cair na tela de login com aviso, em
  // vez de mascarar a expiração caindo no fallback X-Admin-Token.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setSession(null);
      setDevices(null);
      setError(new ApiError("Sessão expirada — faça login novamente.", { status: 401 }));
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Sprint 6.1 — polling do contador de alertas (badge da sidebar).
  // Falha silenciosa: não derruba a UI se a rota não responder.
  useEffect(() => {
    let active = true;
    const fetchCount = () => {
      if (!authedFrom(loadSession(), loadConfig().adminToken, isRecoveryActive())) return; // sem auth
      alertCount()
        .then((r) => { if (active) setAlertsOpen(r?.open || 0); })
        .catch(() => {});
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => { active = false; clearInterval(id); };
  }, []);

  function handleConfigSaved(newCfg) {
    setConfig(newCfg);
    // re-refresh imediato com nova URL/token
    setTimeout(refresh, 0);
  }

  // S3.2 — login concluído (sessão Bearer) ou acesso de recuperação (sem sessão,
  // ADMIN_TOKEN + flag de recovery). Atualiza estado e recarrega.
  // SAAS-AUTH-RECOVERY-MODE-1: login Bearer SUPERA recovery (limpa a flag); o
  // bench já ativou a flag de recovery antes de chamar onLoggedIn(null).
  function handleLoggedIn(newSession) {
    if (newSession) clearRecovery(); // Bearer login supera o modo de recuperação
    setSession(newSession || null);
    setRecovery(isRecoveryActive());
    setConfig(loadConfig());
    // S3.9E — cai na landing permitida à role (tenant não cai no Dashboard).
    setView(defaultViewForRole(newSession?.role));
    setTimeout(refresh, 0);
  }

  // S3.2 — logout: limpa a sessão E o ADMIN_TOKEN de bancada (sign-out total),
  // voltando à tela de login. SAAS-AUTH-RECOVERY-MODE-1: também encerra o modo
  // de recuperação.
  async function handleLogout() {
    await apiLogout();        // best-effort + clearSession()
    clearSession();
    clearRecovery();
    const cfg = saveConfig({ adminToken: "" });
    setSession(null);
    setRecovery(false);
    setConfig(cfg);
    setDevices(null);
  }

  function handleOpenDetail(deviceId, tab) {
    setDetailDeviceId(deviceId);
    setDetailInitialTab(tab || "cockpit");
    setView("detail");
  }

  function handleBackFromDetail() {
    setDetailDeviceId(null);
    setView("devices");
  }

  function handleSetView(v) {
    setView(v);
    if (v !== "detail") setDetailDeviceId(null);
  }

  // S3.2 — guard de rota: sem sessão e sem ADMIN_TOKEN → tela de login.
  if (!authed) {
    // SAAS-PORTAL — cadastro público (não requer sessão).
    if (publicView === "signup") {
      return (
        <SignupPage
          onBack={() => {
            setPublicView(null);
            if (typeof window !== "undefined") window.location.hash = "";
          }}
        />
      );
    }
    return <Login onLoggedIn={handleLoggedIn} onNavigateSignup={() => setPublicView("signup")} />;
  }

  return (
    <CriticalConfirmProvider>
    <AppShell
      currentView={view}
      setView={handleSetView}
      backendOk={backendOk}
      lastRefresh={lastRefresh}
      refreshing={refreshing}
      onRefresh={refresh}
      alertsOpen={alertsOpen}
      session={session}
      onLogout={handleLogout}
    >
      <ErrorBanner
        error={error}
        onOpenSettings={() => handleSetView("settings")}
      />

      <div className="pb-12">
        {/* S3.9E — guarda amigável: view não permitida à role → AccessDenied */}
        {!viewAllowed(view) ? (
          <AccessDenied
            onGoHome={() => handleSetView(defaultViewForRole(session?.role))}
          />
        ) : (
        <>
        {view === "dashboard" && (
          <DashboardNOC
            onNavigate={(target, deviceId, tab) => {
              // Etapa 4A — deep-link p/ DeviceDetail (tabela de
              // problemáticos), mesmo padrão do FleetExplorer.
              if (target === "detail" && deviceId) {
                handleOpenDetail(deviceId, tab);
              } else {
                handleSetView(target);
              }
            }}
          />
        )}
        {view === "devices" && (
          <DevicesList
            devices={devices}
            loading={loading}
            onOpenDetail={handleOpenDetail}
            onRefresh={refresh}
          />
        )}
        {view === "detail" && detailDeviceId && (
          <DeviceDetail
            deviceId={detailDeviceId}
            initialTab={detailInitialTab}
            refreshKey={refreshKey}
            onBack={handleBackFromDetail}
            onError={(err) => setError(err)}
          />
        )}
        {view === "settings" && <Settings onSaved={handleConfigSaved} />}
        {view === "firmware" && <FirmwareReleases />}
        {view === "rollouts" && <Rollouts />}
        {view === "fleet" && (
          <FleetExplorer
            onNavigate={(target, deviceId, tab) => {
              if (target === "detail" && deviceId) {
                handleOpenDetail(deviceId, tab);
              } else {
                handleSetView(target);
              }
            }}
          />
        )}
        {view === "templates" && <ConfigTemplatesPage />}
        {/* Sprint 5.0 — Provisionamento QR + IXC */}
        {view === "provisioning" && <ProvisioningPage />}
        {view === "installations" && <InstallationsPage />}
        {/* Sprint 5.2D — Estoque + Reserva + Logística */}
        {view === "inventory" && <InventoryPage />}
        {/* Sprint 5.3 — Saúde da Frota */}
        {view === "health" && <HealthPage />}
        {/* Sprint 5.5 — Equipe Técnica + Ordens de Serviço */}
        {view === "teams" && <TeamsPage />}
        {/* Sprint 5.6 — Templates Automáticos por Perfil */}
        {view === "rules" && <TemplateRulesPage />}
        {/* Sprint 5.7 — Logs + Auditoria + Timeline */}
        {view === "audit" && <AuditPage />}
        {/* Sprint 5.8 — Observabilidade */}
        {view === "observability" && <ObservabilityPage />}
        {/* Sprint 5.9 — Alertas + Notificações */}
        {view === "alerts" && <AlertsPage onOpenDevice={handleOpenDetail} />}
        {/* Sprint 6.0 — Automação / Playbooks IF-THEN */}
        {view === "automation" && <AutomationPage />}
        {/* Sprint 6.4 — Fleet OTA Manager */}
        {view === "ota" && <OtaManagerPage />}
        {/* NOC-PROD-UI-1 — Painel de Produção (read-only) */}
        {view === "production" && <ProductionPanel />}
        {/* Sprint 6.5 — Multi-tenant / Empresas */}
        {view === "tenants" && <TenantsPage />}
        {view === "tenant-approval" && <TenantApprovalPage />}
        {view === "accounts" && <AccountsPage />}
        {/* SAAS-PORTAL — portal do cliente */}
        {view === "claim-device" && (
          <ClaimDevicePage session={session} onOpenDetail={handleOpenDetail} />
        )}
        {view === "portal-users" && <PortalUsersPage session={session} />}
        </>
        )}
      </div>
    </AppShell>
    </CriticalConfirmProvider>
  );
}

/** S3.9E — placeholder amigável para views não permitidas à role atual. */
function AccessDenied({ onGoHome }) {
  return (
    <div className="flex flex-col items-center justify-center text-center
                    py-24 px-6 gap-3">
      <div className="text-4xl" aria-hidden="true">🔒</div>
      <h2 className="text-lg font-semibold text-noc-text">Acesso restrito</h2>
      <p className="text-sm text-noc-muted max-w-md">
        Esta área é exclusiva de administradores da plataforma. Sua conta
        não tem permissão para vê-la.
      </p>
      <button
        onClick={onGoHome}
        className="mt-1 text-sm px-3 py-1.5 rounded-lg border
                   border-noc-accent/40 bg-noc-accent/15 text-noc-accent
                   font-medium hover:bg-noc-accent/25"
      >
        Voltar ao início
      </button>
    </div>
  );
}
